import {
  areaName,
  ASSET_STATUS_LABELS,
  ASSET_STATUSES,
  ASSET_TYPES,
  assetTypeName,
  BOQ_SCOPE_LABELS,
  BOQ_SCOPES,
  planPoles,
  PRICE_KEYS,
  type Boq,
  type BoqScope,
  type CostAssetRow,
  type CostSummary,
  type LngLat,
  type Paginated,
  type PricingDefaults,
} from '@ftthnms/shared';
import { Badge, Button, EmptyState, RegionChip } from '@ftthnms/ui';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Coins, Download, Layers, Printer, Receipt, Save, Search, Tags, Wallet } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Field, Pager, RequireAbility, Switch, Tabs } from '../components/common';
import { useToast } from '../components/Toast';
import { api, metres, nf, qs, rupiah, send } from '../lib/api';
import { download } from '../lib/download';
import { useSession } from '../lib/session';

type Tab = 'summary' | 'assets' | 'boq' | 'pricing';

/** Biaya & BOQ (PRD §5.9): investment summary, priced assets, bill of quantities and default prices. */
export function CostPage() {
  const [tab, setTab] = useState<Tab>('summary');
  return (
    <RequireAbility action="read" subject="Pricing">
      <div className="fn-page-body">
        <div className="fn-no-print">
          <Tabs<Tab>
            tabs={[
              ['summary', 'Ringkasan'],
              ['assets', 'Detail aset'],
              ['boq', 'BOQ / RAB'],
              ['pricing', 'Harga default'],
            ]}
            value={tab}
            onChange={setTab}
          />
        </div>
        {tab === 'summary' && <SummaryTab onPricing={() => setTab('pricing')} />}
        {tab === 'assets' && <AssetsTab />}
        {tab === 'boq' && <BoqTab />}
        {tab === 'pricing' && <PricingTab />}
      </div>
    </RequireAbility>
  );
}

const rp = (v: number | null | undefined) => (v === null || v === undefined ? '—' : rupiah.format(v));

function scopeLabel(area: string | null, all: boolean) {
  return area ? areaName(area) : all ? 'semua wilayah' : 'wilayah Anda';
}

// ------------------------------------------------------------------ summary

function SummaryTab({ onPricing }: { onPricing: () => void }) {
  const { area, areaOptions } = useSession();
  const summary = useQuery({
    queryKey: ['cost', 'summary', area],
    queryFn: () => api<CostSummary>(`/api/v1/cost/summary${qs({ area })}`),
  });
  const s = summary.data;
  const unpriced = s ? s.byType.reduce((n, t) => n + t.count - t.priced, 0) : 0;
  const kpis = [
    {
      label: 'Total nilai investasi',
      value: rp(s?.grandTotal),
      unit: `aset + kabel · ${scopeLabel(area, areaOptions === 'all')}`,
      icon: Wallet,
      tone: 'primary',
    },
    { label: 'Total aset', value: nf.format(s?.assetCount ?? 0), unit: 'aset', icon: Layers, tone: 'purple' },
    {
      label: 'Jumlah kategori',
      value: nf.format(s?.categories ?? 0),
      unit: 'jenis aset',
      icon: Tags,
      tone: 'cyan',
    },
    { label: 'Harga rata-rata', value: rp(s?.avgPrice), unit: 'per aset', icon: Coins, tone: 'success' },
  ];
  return (
    <>
      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
        {kpis.map(({ label, value, unit, icon: Icon, tone }) => (
          <div key={label} className="fn-card fn-kpi">
            <div className="fn-kpi__head">
              <span className="fn-kpi__label">{label}</span>
              <span className={`fn-tile fn-tile--${tone}`}>
                <Icon className="i" aria-hidden />
              </span>
            </div>
            <div className="fn-kpi__value">
              <span
                className="fn-kpi__number"
                style={{ fontSize: label.startsWith('Total nilai') ? 22 : undefined }}
              >
                {summary.isPending ? '–' : value}
              </span>
            </div>
            <div className="fn-kpi__foot fn-hint">{unit}</div>
          </div>
        ))}
      </div>

      {s && unpriced > 0 && (
        <div className="fn-alert fn-alert--info">
          <div className="fn-alert__body">
            <span className="fn-alert__title">{nf.format(unpriced)} aset belum punya harga sendiri</span>
            <span className="fn-alert__text" style={{ color: 'inherit' }}>
              {s.autoCalculate
                ? 'Nilainya diambil dari harga default per jenis; jenis tanpa harga default belum dihitung.'
                : '"Hitung otomatis" mati, jadi aset tanpa harga dihitung Rp 0.'}{' '}
              <button type="button" className="fn-link" onClick={onPricing}>
                Atur harga default
              </button>
            </span>
          </div>
        </div>
      )}

      <section className="fn-card" style={{ overflow: 'hidden' }}>
        <div className="fn-card__header">
          <div>
            <h3 className="fn-card__title">Nilai per jenis</h3>
            <p className="fn-card__desc">
              Aset yang dibongkar tidak dihitung. Kabel = panjang jalur + cadangan × harga per meter.
            </p>
          </div>
        </div>
        <div className="fn-table-wrap">
          <table className="fn-table">
            <thead>
              <tr>
                <th>Jenis</th>
                <th className="is-num">Jumlah</th>
                <th className="is-num">Berharga sendiri</th>
                <th className="is-num">Total</th>
                <th className="is-num">Rata-rata</th>
                <th className="is-num">Min</th>
                <th className="is-num">Maks</th>
                <th style={{ width: 160 }}>Porsi</th>
              </tr>
            </thead>
            <tbody>
              {s?.byType.map((t) => (
                <tr key={t.typeCode}>
                  <td>{assetTypeName(t.typeCode)}</td>
                  <td className="is-num">{nf.format(t.count)}</td>
                  <td className="is-num">{nf.format(t.priced)}</td>
                  <td className="is-num">{rp(t.total)}</td>
                  <td className="is-num">{rp(t.avg)}</td>
                  <td className="is-num">{rp(t.min)}</td>
                  <td className="is-num">{rp(t.max)}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <div className="fn-bar" style={{ flex: 1 }}>
                        <i style={{ width: `${t.pct}%` }} />
                      </div>
                      <span className="num fn-hint" style={{ width: 44, textAlign: 'right' }}>
                        {t.pct}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
              {s && (
                <tr>
                  <td>Kabel fiber ({nf.format(s.cable.routes)} jalur)</td>
                  <td className="is-num">{metres(s.cable.lengthM)}</td>
                  <td />
                  <td className="is-num">{rp(s.cable.value)}</td>
                  <td colSpan={4} />
                </tr>
              )}
              {s && (
                <tr style={{ fontWeight: 700 }}>
                  <td>Total</td>
                  <td className="is-num">{nf.format(s.assetCount)}</td>
                  <td />
                  <td className="is-num">{rp(s.grandTotal)}</td>
                  <td colSpan={4} />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

// ------------------------------------------------------------------ priced assets

function AssetsTab() {
  const { area } = useSession();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [sort, setSort] = useState('code');
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState<string | null>(null);
  const pageSize = 50;
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => setPage(1), [debouncedQ, type, status, sort, area]);
  const filter = { area, q: debouncedQ, type, status, sort };
  const list = useQuery({
    queryKey: ['cost', 'assets', filter, page],
    queryFn: () => api<Paginated<CostAssetRow>>(`/api/v1/cost/assets${qs({ ...filter, page, pageSize })}`),
    placeholderData: keepPreviousData,
  });

  async function exportAs(format: 'xlsx' | 'csv') {
    setBusy(format);
    try {
      await download(`/api/v1/cost/assets${qs({ ...filter, format })}`, `ftthnms-biaya.${format}`);
    } catch (err) {
      toast({ tone: 'danger', text: (err as Error).message });
    } finally {
      setBusy(null);
    }
  }

  const data = list.data;
  return (
    <section className="fn-card" style={{ overflow: 'hidden' }}>
      <div className="fn-toolbar fn-no-print">
        <div className="fn-input-group" style={{ flex: 1, maxWidth: 260, minWidth: 160 }}>
          <Search className="i" aria-hidden />
          <input
            className="fn-input"
            aria-label="Cari aset"
            placeholder="Cari kode, nama, model…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <select
          className="fn-input"
          aria-label="Jenis"
          style={{ width: 'auto' }}
          value={type}
          onChange={(e) => setType(e.target.value)}
        >
          <option value="">Semua jenis</option>
          {ASSET_TYPES.map((t) => (
            <option key={t.code} value={t.code}>
              {t.name}
            </option>
          ))}
        </select>
        <select
          className="fn-input"
          aria-label="Status"
          style={{ width: 'auto' }}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">Semua status</option>
          {ASSET_STATUSES.map((s) => (
            <option key={s} value={s}>
              {ASSET_STATUS_LABELS[s]}
            </option>
          ))}
        </select>
        <select
          className="fn-input"
          aria-label="Urutkan"
          style={{ width: 'auto' }}
          value={sort}
          onChange={(e) => setSort(e.target.value)}
        >
          <option value="code">Urut kode</option>
          <option value="type">Urut jenis</option>
          <option value="price_desc">Harga tertinggi</option>
          <option value="price_asc">Harga terendah</option>
        </select>
        <span className="fn-toolbar__spacer" />
        <Button
          size="sm"
          icon={<Download className="i" aria-hidden />}
          loading={busy === 'xlsx'}
          onClick={() => void exportAs('xlsx')}
        >
          Excel
        </Button>
        <Button size="sm" loading={busy === 'csv'} onClick={() => void exportAs('csv')}>
          CSV
        </Button>
        <Button size="sm" icon={<Printer className="i" aria-hidden />} onClick={() => window.print()}>
          Cetak
        </Button>
      </div>
      <div className="fn-table-wrap">
        <table className="fn-table">
          <thead>
            <tr>
              <th>Aset</th>
              <th>Jenis</th>
              <th>Model</th>
              <th>Status</th>
              <th className="is-num">Harga</th>
              <th>Sumber</th>
            </tr>
          </thead>
          <tbody>
            {data?.items.map((a) => (
              <tr key={a.id}>
                <td>
                  <div className="fn-asset__name">{a.name}</div>
                  <div className="fn-asset__code">
                    {a.code}
                    {a.areaCode && (
                      <span style={{ marginLeft: 6 }}>
                        <RegionChip code={a.areaCode} />
                      </span>
                    )}
                  </div>
                </td>
                <td>{assetTypeName(a.typeCode)}</td>
                <td>{a.model ?? <span className="fn-hint">—</span>}</td>
                <td>{ASSET_STATUS_LABELS[a.status as keyof typeof ASSET_STATUS_LABELS] ?? a.status}</td>
                <td className="is-num">{rp(a.price)}</td>
                <td>
                  {a.priceSource === 'asset' ? (
                    <Badge tone="success">Harga aset</Badge>
                  ) : a.priceSource === 'default' ? (
                    <Badge>Default</Badge>
                  ) : (
                    <Badge tone="warning">Belum ada</Badge>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager total={data?.total ?? 0} unit="aset" page={page} pageSize={pageSize} onPage={setPage} />
    </section>
  );
}

// ------------------------------------------------------------------ BOQ / RAB

function BoqTab() {
  const { area, areaOptions } = useSession();
  const toast = useToast();
  const [scope, setScope] = useState<BoqScope>('planned');
  const [busy, setBusy] = useState(false);
  const boq = useQuery({
    queryKey: ['cost', 'boq', area, scope],
    queryFn: () => api<Boq>(`/api/v1/cost/boq${qs({ area, scope })}`),
  });
  const b = boq.data;
  const groups = useMemo(() => {
    const g = new Map<string, Boq['lines']>();
    for (const l of b?.lines ?? []) g.set(l.group, [...(g.get(l.group) ?? []), l]);
    return [...g.entries()];
  }, [b]);

  async function exportXlsx() {
    setBusy(true);
    try {
      await download(`/api/v1/cost/boq${qs({ area, scope, format: 'xlsx' })}`, 'ftthnms-boq.xlsx');
    } catch (err) {
      toast({ tone: 'danger', text: (err as Error).message });
    } finally {
      setBusy(false);
    }
  }

  let no = 0;
  return (
    <section className="fn-card" style={{ overflow: 'hidden' }}>
      <div className="fn-card__header">
        <div>
          <h3 className="fn-card__title">BOQ / RAB · {scopeLabel(area, areaOptions === 'all')}</h3>
          <p className="fn-card__desc">
            Rekap material dari aset dan jalur kabel × harga satuan, ditambah jasa pemasangan. Kolom Rencana
            dan Terpasang untuk perbandingan.
          </p>
        </div>
        <div className="flex items-center gap-2 fn-no-print">
          <div className="fn-segmented" role="tablist" aria-label="Cakupan BOQ">
            {BOQ_SCOPES.map((s) => (
              <button
                key={s}
                type="button"
                className="fn-segmented__item"
                aria-pressed={scope === s}
                onClick={() => setScope(s)}
              >
                {BOQ_SCOPE_LABELS[s]}
              </button>
            ))}
          </div>
          <Button
            size="sm"
            icon={<Download className="i" aria-hidden />}
            loading={busy}
            onClick={() => void exportXlsx()}
          >
            Excel
          </Button>
          <Button size="sm" icon={<Printer className="i" aria-hidden />} onClick={() => window.print()}>
            Cetak
          </Button>
        </div>
      </div>
      {b && b.lines.length === 0 ? (
        <div style={{ padding: 20 }}>
          <EmptyState
            icon={<Receipt className="i i--xl" aria-hidden />}
            title={`Tidak ada item ${BOQ_SCOPE_LABELS[scope].toLowerCase()}`}
            text="BOQ disusun dari aset dan jalur kabel di wilayah yang dipilih."
          />
        </div>
      ) : (
        <div className="fn-table-wrap">
          <table className="fn-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>No</th>
                <th>Uraian</th>
                <th>Satuan</th>
                <th className="is-num">Rencana</th>
                <th className="is-num">Terpasang</th>
                <th className="is-num">Volume</th>
                <th className="is-num">Harga satuan</th>
                <th className="is-num">Jumlah</th>
              </tr>
            </thead>
            <tbody>
              {groups.map(([group, lines]) => (
                <GroupLines key={group} group={group}>
                  {lines.map((l) => (
                    <tr key={l.item}>
                      <td className="num">{++no}</td>
                      <td>
                        {l.item}
                        {l.missingPrice && (
                          <span
                            style={{ marginLeft: 6 }}
                            title="Sebagian item belum punya harga (aset maupun default)"
                          >
                            <Badge tone="warning">harga belum lengkap</Badge>
                          </span>
                        )}
                      </td>
                      <td>{l.unit}</td>
                      <td className="is-num">{nf.format(l.qtyPlanned)}</td>
                      <td className="is-num">{nf.format(l.qtyInstalled)}</td>
                      <td className="is-num" style={{ fontWeight: 700 }}>
                        {nf.format(l.qty)}
                      </td>
                      <td className="is-num">{rp(l.unitPrice)}</td>
                      <td className="is-num">{rp(l.total)}</td>
                    </tr>
                  ))}
                </GroupLines>
              ))}
              {b && (
                <>
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'right' }}>
                      Subtotal material
                    </td>
                    <td className="is-num">{rp(b.material)}</td>
                  </tr>
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'right' }}>
                      Jasa pemasangan ({nf.format(b.laborPct)}%)
                    </td>
                    <td className="is-num">{rp(b.labor)}</td>
                  </tr>
                  <tr style={{ fontWeight: 800 }}>
                    <td colSpan={7} style={{ textAlign: 'right' }}>
                      Total RAB
                    </td>
                    <td className="is-num">{rp(b.total)}</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function GroupLines({ group, children }: { group: string; children: ReactNode }) {
  return (
    <>
      <tr>
        <td colSpan={8} className="fn-label" style={{ background: 'var(--bg-surface-inset)' }}>
          {group}
        </td>
      </tr>
      {children}
    </>
  );
}

// ------------------------------------------------------------------ default prices

/** 100 m straight line going north, for the "jalur 100 m ≈ N tiang" preview (ACC-05). */
const SAMPLE_PATH: LngLat[] = [
  [107, -6.9],
  [107, -6.9 + 100 / 111_320],
];

function PricingTab() {
  const { ability } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const canEdit = ability.can('manage', 'Pricing');
  const current = useQuery({
    queryKey: ['cost', 'pricing'],
    queryFn: () => api<PricingDefaults>('/api/v1/cost/pricing'),
  });
  const [draft, setDraft] = useState<{ prices: Record<string, string>; autoCalculate: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const [interval, setIntervalM] = useState(30);

  const base = useMemo(
    () =>
      current.data
        ? {
            prices: Object.fromEntries(
              Object.entries(current.data.prices).map(([k, v]) => [k, v === null ? '' : String(v)]),
            ),
            autoCalculate: current.data.autoCalculate,
          }
        : null,
    [current.data],
  );
  const v = draft ?? base;
  const groups = useMemo(() => {
    const g = new Map<string, typeof PRICE_KEYS>();
    for (const k of PRICE_KEYS) g.set(k.group, [...(g.get(k.group) ?? []), k]);
    return [...g.entries()];
  }, []);
  if (!v) return null;

  const parse = (s: string) => (s.trim() === '' ? null : Number(s.replace(/\./g, '').replace(',', '.')));
  const poles = planPoles(SAMPLE_PATH, { intervalM: interval, atTurns: true }).length;
  const poleUnit = parse(v.prices.pole_unit ?? '');
  const cablePerM = parse(v.prices.cable_per_m ?? '');
  const dirty = JSON.stringify(v) !== JSON.stringify(base);

  async function save() {
    setBusy(true);
    try {
      const prices = Object.fromEntries(Object.entries(v!.prices).map(([k, s]) => [k, parse(s)]));
      if (Object.values(prices).some((p) => p !== null && !Number.isFinite(p)))
        throw new Error('Ada harga yang bukan angka.');
      qc.setQueryData(
        ['cost', 'pricing'],
        await send<PricingDefaults>('PUT', '/api/v1/cost/pricing', {
          prices,
          autoCalculate: v!.autoCalculate,
        }),
      );
      await qc.invalidateQueries({ queryKey: ['cost'] });
      setDraft(null);
      toast({ tone: 'success', text: 'Harga default tersimpan.' });
    } catch (err) {
      toast({ tone: 'danger', text: (err as Error).message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid gap-4" style={{ gridTemplateColumns: 'minmax(0, 1fr) 320px', alignItems: 'start' }}>
      <section className="fn-card">
        <div className="fn-card__header">
          <div>
            <h3 className="fn-card__title">Harga default</h3>
            <p className="fn-card__desc">
              Dipakai untuk aset tanpa harga sendiri, kabel dengan tipe tanpa harga per meter, dan tiang
              otomatis.
              {current.data?.updatedBy && ` Terakhir diubah ${current.data.updatedBy}.`}
            </p>
          </div>
          {canEdit && (
            <Button
              size="sm"
              variant="primary"
              icon={<Save className="i" aria-hidden />}
              disabled={!dirty}
              loading={busy}
              onClick={() => void save()}
            >
              Simpan
            </Button>
          )}
        </div>
        <div className="fn-card__body flex flex-col gap-5">
          <label className="fn-choice" style={{ cursor: canEdit ? 'pointer' : 'default' }}>
            <span>
              <span className="fn-choice__title">Hitung otomatis</span>
              <span className="fn-choice__desc">Aset tanpa harga dinilai dengan harga default jenisnya.</span>
            </span>
            <Switch
              label="Hitung otomatis"
              checked={v.autoCalculate}
              disabled={!canEdit}
              onChange={(on) => setDraft({ ...v, autoCalculate: on })}
            />
          </label>
          {groups.map(([group, keys]) => (
            <div key={group}>
              <div className="fn-panel__label" style={{ marginBottom: 8 }}>
                {group}
              </div>
              <div className="fn-form-grid">
                {keys.map((k) => (
                  <Field key={k.key} label={k.label} hint={k.unit}>
                    <div className="fn-input-group">
                      {k.key !== 'labor_pct' && <span className="fn-hint">Rp</span>}
                      <input
                        className="fn-input"
                        inputMode="numeric"
                        disabled={!canEdit}
                        placeholder="belum diisi"
                        value={v.prices[k.key] ?? ''}
                        onChange={(e) => setDraft({ ...v, prices: { ...v.prices, [k.key]: e.target.value } })}
                        style={{ textAlign: 'right' }}
                      />
                      {k.key === 'labor_pct' && <span className="fn-input-group__suffix">%</span>}
                    </div>
                  </Field>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
      <section className="fn-card">
        <div className="fn-card__header">
          <div>
            <h3 className="fn-card__title">Contoh estimasi</h3>
            <p className="fn-card__desc">Jalur lurus 100 m dengan tiang otomatis.</p>
          </div>
        </div>
        <div className="fn-card__body flex flex-col gap-3">
          <Field label="Jarak antar tiang">
            <select
              className="fn-input"
              value={interval}
              onChange={(e) => setIntervalM(Number(e.target.value))}
            >
              {[20, 25, 30, 40, 50].map((m) => (
                <option key={m} value={m}>
                  {m} m
                </option>
              ))}
            </select>
          </Field>
          <dl className="fn-dl">
            <dt>Tiang</dt>
            <dd className="num">
              {poles} × {rp(poleUnit)}
            </dd>
            <dt>Kabel</dt>
            <dd className="num">100 m × {rp(cablePerM)}</dd>
            <dt>Estimasi</dt>
            <dd className="num" style={{ fontWeight: 800 }}>
              {rp((poleUnit ?? 0) * poles + (cablePerM ?? 0) * 100)}
            </dd>
          </dl>
          <p className="fn-hint">Tiang di awal dan akhir jalur tidak dihitung (aset ujung).</p>
        </div>
      </section>
    </div>
  );
}
